/* ==========================================================================
   Klaviyo Back-In-Stock - global, event-delegated controller
   --------------------------------------------------------------------------
   Loaded site-wide from theme.liquid (defer). It MUST live in an external asset
   rather than inline in the snippet: the quick-buy drawer/popover injects its
   markup via innerHTML, and browsers do not execute <script> inserted that way.
   A global, delegated controller binds once on page load and therefore also
   governs BIS forms that appear later inside an injected quick-buy.

   Flow (two-step):
     1. Email step (.klaviyo-bis-inline__form): join the waitlist by email, plus an
        optional email-marketing opt-in. This is the only ask up front, so the phone
        request never depresses the email capture that actually delivers the alert.
     2. SMS step (.klaviyo-bis-inline__sms-form): revealed only after the email step
        succeeds. Collects the phone with its own explicit, separately-submitted
        consent, or is skipped via "No thanks". Cleaner for TCPA than a pre-checked box.

   Submit is handled via delegation (capture phase) so injected forms work and the
   email never triggers a cart add.
   ========================================================================== */
(function () {
  'use strict';
  if (window.KlaviyoBISInitialized) return;
  window.KlaviyoBISInitialized = true;

  var KLAVIYO_COMPANY_ID = 'xymHPB';
  var KLAVIYO_LIST_ID = 'tHvrLZ';      // Email marketing list
  var KLAVIYO_SMS_LIST_ID = 'XE9bT6';  // TEM - SMS List (marketing SMS opt-in)
  var KLAVIYO_REVISION = '2024-06-15';
  // The Create Client Subscription schema only accepts the `subscriptions`
  // object inside profile attributes from revision 2025-01-15 onward; the
  // older 2024-06-15 schema rejects it. Pin the SMS subscription call here.
  var KLAVIYO_SUBSCRIPTION_REVISION = '2026-04-15';

  // --- Visibility: react to variant changes (incl. inside quick-buy) ---------
  function updateVisibility(detail) {
    if (!detail || !detail.variant) return;
    var available = detail.variant.available;

    document.querySelectorAll('.klaviyo-bis-inline').forEach(function (container) {
      container.dataset.variantId = detail.variant.id;
      container.dataset.variantTitle = detail.variant.title || '';
      container.style.display = available ? 'none' : 'block';
      if (!available) resetForm(container);
    });

    // Mirror the ATC wrapper, but never override preorder-managed variants.
    document.querySelectorAll('[data-atc-wrapper]').forEach(function (wrapper) {
      var form = wrapper.closest('[data-preorder-form]');
      var preorderMap = form ? form.querySelector('[data-preorder-map]') : null;
      var isPreorder = false;
      if (preorderMap && detail.variant) {
        try {
          var map = JSON.parse(preorderMap.textContent);
          isPreorder = !!(map[String(detail.variant.id)] && map[String(detail.variant.id)].enabled);
        } catch (e) {}
      }
      if (!isPreorder) wrapper.style.display = available ? '' : 'none';
    });
  }

  function resetForm(container) {
    var emailForm = container.querySelector('.klaviyo-bis-inline__form');
    var smsForm = container.querySelector('.klaviyo-bis-inline__sms-form');
    var success = container.querySelector('.klaviyo-bis-inline__success');

    if (emailForm) {
      emailForm.reset();
      emailForm.hidden = false;
      var subscribe = emailForm.querySelector('input[name="subscribe_to_list"]');
      if (subscribe) subscribe.checked = true;
      var emailError = emailForm.querySelector('.klaviyo-bis-inline__error');
      if (emailError) emailError.textContent = '';
    }
    if (smsForm) {
      smsForm.reset();
      smsForm.hidden = true;
      var smsError = smsForm.querySelector('.klaviyo-bis-inline__error');
      if (smsError) smsError.textContent = '';
    }
    if (success) success.hidden = true;
  }

  document.addEventListener('variant:changed', function (e) { updateVisibility(e.detail); });

  // --- Helpers ---------------------------------------------------------------
  function klaviyoPost(path, payload, revision) {
    return fetch(
      'https://a.klaviyo.com/client/' + path + '/?company_id=' + KLAVIYO_COMPANY_ID,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': revision || KLAVIYO_REVISION },
        body: JSON.stringify(payload)
      }
    );
  }

  function setBusy(button, busy) {
    if (!button) return;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.disabled = busy;
  }

  function formatPhone(raw) {
    var digits = (raw || '').replace(/\D/g, '');
    if (digits.length === 11 && digits[0] === '1') return '+' + digits;
    if (digits.length === 10) return '+1' + digits;
    return null;
  }

  function showSuccess(container, withSms) {
    var emailForm = container.querySelector('.klaviyo-bis-inline__form');
    var smsForm = container.querySelector('.klaviyo-bis-inline__sms-form');
    var success = container.querySelector('.klaviyo-bis-inline__success');
    var successText = success && success.querySelector('.klaviyo-bis-inline__success-text');
    if (emailForm) emailForm.hidden = true;
    if (smsForm) smsForm.hidden = true;
    if (successText) {
      successText.textContent = withSms
        ? "You're all set. We'll email you, and text you, the moment it's back in stock."
        : "You're on the list! We'll email you when this item is back in stock.";
    }
    if (success) {
      success.hidden = false;
      if (typeof success.focus === 'function') success.focus();
    }
  }

  // --- Submit (delegated, capture phase so it runs before any cart handler) ---
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.classList) return;
    if (form.classList.contains('klaviyo-bis-inline__form')) { handleEmailStep(e, form); }
    else if (form.classList.contains('klaviyo-bis-inline__sms-form')) { handleSmsStep(e, form); }
  }, true);

  // --- Skip the SMS step (delegated) -----------------------------------------
  document.addEventListener('click', function (e) {
    var skip = e.target.closest && e.target.closest('.klaviyo-bis-inline__skip');
    if (!skip) return;
    e.preventDefault();
    var container = skip.closest('.klaviyo-bis-inline');
    if (container) showSuccess(container, false);
  });

  // --- Step 1: email waitlist signup -----------------------------------------
  async function handleEmailStep(event, form) {
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    var container = form.closest('.klaviyo-bis-inline');
    var emailInput = form.querySelector('input[type="email"]');
    var subscribeCheckbox = form.querySelector('input[name="subscribe_to_list"]');
    var submitButton = form.querySelector('button[type="submit"]');
    var errorEl = form.querySelector('.klaviyo-bis-inline__error');

    var email = emailInput.value.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errorEl.textContent = email ? 'Please enter a valid email address.' : 'Please enter your email address.';
      emailInput.focus();
      return;
    }

    errorEl.textContent = '';
    setBusy(submitButton, true);

    var variantId = container.dataset.variantId;
    var subscribeToNewsletter = (subscribeCheckbox && subscribeCheckbox.checked) || false;

    try {
      if (window._learnq) window._learnq.push(['identify', { '$email': email }]);

      var bisPayload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: email } } },
            channels: ['EMAIL']
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
          }
        }
      };

      var response = await klaviyoPost('back-in-stock-subscriptions', bisPayload);
      if (!response.ok && response.status !== 202) {
        var errorText = await response.text();
        console.error('Klaviyo BIS API Error:', response.status, errorText);
        throw new Error('Subscription failed');
      }

      if (subscribeToNewsletter) {
        try {
          await klaviyoPost('subscriptions', {
            data: {
              type: 'subscription',
              attributes: { profile: { data: { type: 'profile', attributes: { email: email } } } },
              relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST_ID } } }
            }
          });
        } catch (e2) { console.log('Newsletter subscription attempt:', e2); }
      }

      // Email signup succeeded - reveal the optional SMS step.
      form.hidden = true;
      var smsForm = container.querySelector('.klaviyo-bis-inline__sms-form');
      if (smsForm) {
        smsForm.hidden = false;
        var phoneInput = smsForm.querySelector('input[type="tel"]');
        if (phoneInput) phoneInput.focus();
      } else {
        showSuccess(container, false);
      }
    } catch (error) {
      console.error('Klaviyo BIS error:', error);
      errorEl.textContent = 'Something went wrong. Please try again.';
    } finally {
      setBusy(submitButton, false);
    }
  }

  // --- Step 2: optional SMS opt-in -------------------------------------------
  async function handleSmsStep(event, form) {
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    var container = form.closest('.klaviyo-bis-inline');
    var phoneInput = form.querySelector('input[type="tel"]');
    var submitButton = form.querySelector('button[type="submit"]');
    var errorEl = form.querySelector('.klaviyo-bis-inline__error');
    var emailInput = container.querySelector('.klaviyo-bis-inline__form input[type="email"]');
    var email = emailInput ? emailInput.value.trim() : '';

    var phone = formatPhone(phoneInput && phoneInput.value);
    if (!phone) {
      errorEl.textContent = 'Please enter a valid 10-digit US mobile number.';
      if (phoneInput) phoneInput.focus();
      return;
    }

    errorEl.textContent = '';
    setBusy(submitButton, true);

    try {
      if (!KLAVIYO_SMS_LIST_ID) throw new Error('SMS list not configured');

      var smsProfileAttrs = { phone_number: phone };
      if (email) smsProfileAttrs.email = email;
      smsProfileAttrs.subscriptions = { sms: { marketing: { consent: 'SUBSCRIBED' } } };

      var smsPayload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in stock - PDP',
            profile: { data: { type: 'profile', attributes: smsProfileAttrs } }
          },
          relationships: { list: { data: { type: 'list', id: KLAVIYO_SMS_LIST_ID } } }
        }
      };

      var response = await klaviyoPost('subscriptions', smsPayload, KLAVIYO_SUBSCRIPTION_REVISION);
      if (!response.ok && response.status !== 202) {
        var errorText = await response.text();
        console.error('Klaviyo SMS API Error:', response.status, errorText);
        throw new Error('SMS subscription failed');
      }

      showSuccess(container, true);
    } catch (error) {
      // The SMS save failed - most commonly because Klaviyo SMS is not yet
      // provisioned (no enabled sending number / 10DLC). Don't block the shopper:
      // they're already on the email waitlist, so fall through to the email
      // confirmation and log the detail (status + body is logged above) for debugging.
      console.error('Klaviyo SMS error:', error);
      showSuccess(container, false);
    } finally {
      setBusy(submitButton, false);
    }
  }

  window.KlaviyoBIS = { updateVisibility: updateVisibility };
})();